import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { AuthorizationEvidence, RelatedEvent } from '../../api/areas/core';
import { DataTable, type Column } from '../../components/blocks';
import { CorrelationLink } from '../../components/Ids';
import { KeyValue, type KeyValueRow } from '../../components/KeyValue';
import { EmptyState } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { ActorName, codeClass, useVocab } from '../domains/refs';
import { humanize } from '../domains/status';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Authorisation evidence (method, times, actor types; per side for registrant changes). */
export function EvidenceView({ evidence, empty }: { evidence: AuthorizationEvidence; empty: string }) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const vocab = useVocab();
  const rows = (o: Record<string, unknown>, withConfirmation: boolean): KeyValueRow[] => {
    const text = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : null);
    const method = text('method');
    const channel = text('channel');
    const confirmed = text('confirmed_at');
    const actor = text('actor_type');
    const agreement = text('agreement_version');
    return [
      method ? [t('evidence.method'), <span key="m" title={method}>{vocab('method', method)}</span>] : null,
      channel ? [t('evidence.channel'), vocab('channel', channel)] : null,
      withConfirmation ? [t('evidence.confirmedAt'), confirmed ? formatDateTime(confirmed) : <span key="c" className="text-muted-foreground">{t('evidence.notConfirmed')}</span>] : null,
      // Before a side confirms, `actor_type` is only who is expected to; it is not a confirmer yet.
      actor && (!withConfirmation || confirmed) ? [t('evidence.actorType'), <ActorName key="a" type={actor} name={text('actor_name')} />] : null,
      agreement ? [t('evidence.agreementVersion'), <code key="a" className={codeClass}>{agreement}</code>] : null,
    ];
  };
  const sides = Object.entries(evidence).filter((e): e is [string, Record<string, unknown>] => isRecord(e[1]));
  const top = rows(evidence, false).filter(Boolean);
  if (!top.length && !sides.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="grid grid-cols-1 gap-4">
      {top.length ? <KeyValue columns={2} rows={top} /> : null}
      {sides.length ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {sides.map(([side, value]) => (
            <div key={side} className="rounded-lg border p-3">
              <div className="mb-2 text-sm font-semibold">{tr(`evidence.side.${side}`, { defaultValue: humanize(side) })}</div>
              <KeyValue rows={rows(value, true)} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Domain events related to a transfer or registrant change. */
export function RelatedEvents({ events, empty, historyHref }: { events: RelatedEvent[]; empty: string; historyHref: string }) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const columns: Column<RelatedEvent>[] = [
    { key: 'when', header: t('events.when'), className: 'whitespace-nowrap', cell: (e) => formatDateTime(e.occurred_at) },
    {
      key: 'event',
      header: t('events.event'),
      cell: (e) => (
        <Link to={`${historyHref}#${encodeURIComponent(e.id)}`} className="font-medium text-primary hover:underline">
          {L.eventType(e.event_type)}
        </Link>
      ),
    },
    { key: 'actor', header: t('events.actor'), cell: (e) => <ActorName type={e.actor_type} name={e.actor_name} /> },
    { key: 'corr', header: t('events.correlation'), cell: (e) => <CorrelationLink id={e.correlation_id} /> },
  ];
  return <DataTable columns={columns} rows={events} rowKey={(e) => e.id} empty={<EmptyState title={empty} icon={History} />} />;
}

/** Keyset paging state kept in the URL: the cursor stack lives in memory, the current cursor in `?before=`. */
export function nextParams(params: URLSearchParams, next: Record<string, string>): URLSearchParams {
  const p = new URLSearchParams(params);
  for (const [k, v] of Object.entries(next)) {
    if (v) p.set(k, v);
    else p.delete(k);
  }
  return p;
}
